import * as Sentry from "@sentry/react"
import { FC, useEffect } from "react"
import "./App.css"
import PublicHomePage from "./components/PublicHomePage"

import { Alert, Box } from "@mui/material"
import "react-json-view-lite/dist/index.css"
import { useSelector } from "react-redux"
import { Route, Routes, useLocation } from "react-router-dom"
import { useAppAuth } from "./auth"
import { resolveUiId } from "./routeTitles"
import { AuthLoadedRoute } from "./components/AuthLoadedRoute"
import FeaturePage from "./components/FeaturePage"
import LoginPage from "./components/LoginPage"
import NotAccessiblePage from "./components/NotAccessiblePage"
import NotFound from "./components/NotFound"
import OsClientPage from "./components/OsClientPage"
import { OsConfigurationPage } from "./components/OsConfigurationPage"
import OsHomePage from "./components/OsHomePage"
import OsIamPage from "./components/OsIamPage"
import OsLockerControllerPage from "./components/OsLockerControllerPage"
import OsLockerPage from "./components/OsLockerPage"
import OsLockerWidget from "./components/OsLockerWidget"
import OsLockersPage from "./components/OsLockersPage"
import OsLockersPageEmbedded from "./components/OsLockersPageEmbedded"
import OsReportingPage from "./components/OsReportingPage"
import OsReservationsPage from "./components/OsReservationsPage"
import PrivateHomePage from "./components/PrivateHomePage"
import { ProtectedRoute } from "./components/ProtectedRoute"
import SaAdminLogsPage from "./components/SaAdminLogsPage"
import SaAdministratorPage from "./components/SaAdministratorPage"
import SaAdministratorsPage from "./components/SaAdministratorsPage"
import SaDeleteThingsPage from "./components/SaDeleteThingsPage"
import SaEmailExpeditorsPage from "./components/SaEmailExpeditorsPage"
import SaFeaturesPage from "./components/SaFeaturesPage"
import SaHomePage from "./components/SaHomePage"
import SaLockerPage from "./components/SaLockerPage"
import SaLockersPage from "./components/SaLockersPage"
import SaMeventsPage from "./components/SaMeventsPage"
import SaNotifDeliveriesPage from "./components/SaNotifDeliveriesPage"
import SaOrgMembersPage from "./components/SaOrgMembersPage"
import SaReservationAudPage from "./components/SaReservationAudPage"
import SaReservationsPage from "./components/SaReservationsPage"
import SaSeedTrackersPage from "./components/SaSeedTrackersPage"
import SaSmsExpeditorsPage from "./components/SaSmsExpeditorsPage"
import SaSomumPage from "./components/SaSomumPage"
import SaSstPage from "./components/SaSstPage"
import SaSstv2Page from "./components/SaSstv2Page"
import SaSstv2LockerPage from "./components/SaSstv2LockerPage"
import SaMonthlyReportPage, {
  MonthlyExtractLinkRedirect,
} from "./components/SaMonthlyReportPage"
import SaMonthlyReportsPage from "./components/SaMonthlyReportsPage"
import SaReservationSyncPage from "./components/SaReservationSyncPage"
import SaReservationSyncsPage from "./components/SaReservationSyncsPage"
import SaSl1ImportPage from "./components/SaSl1ImportPage"
import SaSl1ImportReportPage from "./components/SaSl1ImportReportPage"
import SaSstv2MigrationPage from "./components/SaSstv2MigrationPage"
import Sstv2TraceDetailPage from "./features/sstv2-trace/Sstv2TraceDetailPage"
import SaTenantPage from "./components/SaTenantPage"
import SaTenantsPage from "./components/SaTenantsPage"
import SaUsersPage from "./components/SaUsersPage"
import { SignOutPage } from "./components/SignOutPage"
import SignupInfo from "./components/SignupInfo"
import UserOrgsPage from "./components/UsOrgsPage"
import UserProfilePage from "./components/UsProfilePage"
import { useGetMeQuery } from "./features/me/meApi"
import {
  selectIsMblsAuthenticated,
  selectMe,
  selectMeErrorMessage,
} from "./features/me/meSlice"
import { useDocumentTitle } from "./hooks/useDocumentTitle"
import { useSearchParamsState } from "./hooks/useSearchParamsState"
import i18n from "./i18n/i18n"
import { consoleDebug, getAuthStrategy } from "./utils"

consoleDebug("VITE_BASE_PATH", import.meta.env.VITE_BASE_PATH)
const rawDisableSentry = import.meta.env.VITE_DISABLE_SENTRY
const isSentryDisabled =
  typeof rawDisableSentry === "string" &&
  rawDisableSentry.toLowerCase() === "true"

const SentryRoutes = isSentryDisabled
  ? Routes
  : Sentry.withSentryReactRouterV7Routing(Routes)
const isEmbedded = window.self !== window.top

const App: FC = () => {
  const { user } = useSelector(selectMe)
  const isMblsAuthenticated = useSelector(selectIsMblsAuthenticated)
  const errorMessage = useSelector(selectMeErrorMessage)

  const isSuperAdmin = user?.isSuperAdmin

  const { isAuthenticated, isProcessed, isProcessing, isReady } = useAppAuth()

  const sl2ReadyToCheckAuth = isAuthenticated && isReady

  useGetMeQuery("noId", {
    skip: !sl2ReadyToCheckAuth,
  })

  // In embedded mode (iframe), show error inline instead of redirecting to /login
  const embeddedErrorFallback = isEmbedded ? (
    <Box data-ui="UI-SL2W-016"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        p: 2,
      }}
    >
      <Alert severity="error">
        {errorMessage || "Authentication failed. Please try again."}
      </Alert>
    </Box>
  ) : undefined

  // Per-route browser-tab title. Mounted once; no-ops while iframed (LOCKER-409).
  useDocumentTitle()

  // Route-level `data-ui` surface marker — stamped iframed too (routeTitles.ts).
  const { pathname } = useLocation()

  // get locale from url — an explicit override only. Empty default so we can
  // distinguish "no ?locale= param" (a bare refresh) from an actual value;
  // otherwise the default would clobber the persisted preference every load.
  const [locale] = useSearchParamsState("locale", "")

  useEffect(() => {
    // be permissive for locale, anything containing 'fr' will be considered as 'fr'
    // and anything containing 'en' will be considered as 'en'. Only resolved when
    // the URL actually carries a locale param.
    const _locale = locale?.toLowerCase()
    let urlLanguage: string | undefined
    if (_locale?.includes("fr")) {
      urlLanguage = "fr"
    } else if (_locale?.includes("en")) {
      urlLanguage = "en"
    }

    // Priority: explicit URL locale > persisted preference (localStorage, already
    // loaded by i18n init) > browser language > fr.
    const language =
      urlLanguage ||
      localStorage.getItem("language") ||
      navigator.language.split("-")[0] ||
      "fr"
    // Persist the resolved choice so it survives refreshes even when it came
    // from the URL override.
    localStorage.setItem("language", language)
    i18n.changeLanguage(language)

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "language") {
        i18n.changeLanguage(event.newValue || "fr")
      }
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [locale])

  return (
    <div data-ui={resolveUiId(pathname)} style={{ display: "contents" }}>
    <SentryRoutes>
      <Route element={<AuthLoadedRoute isReady={isReady} />}>
        <Route path="" element={<PublicHomePage />} />
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/signup-info" element={<SignupInfo />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signout" element={<SignOutPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            name="OS routes"
            isAllowed={isAuthenticated && isMblsAuthenticated !== false}
            isReady={
              isReady && (isMblsAuthenticated !== undefined || !isAuthenticated)
            }
            errorFallback={embeddedErrorFallback}
          />
        }
      >
        <Route path="/us" element={<PrivateHomePage />} />
        <Route path="/os" element={<OsHomePage />} />
        <Route path="/os/iam" element={<OsIamPage />} />
        <Route path="/os/lockerWidget" element={<OsLockerWidget />} />
        <Route path="/os/lockers/:lockerId" element={<OsLockerPage />} />
        <Route
          path="/os/lockers/:lockerId/controller"
          element={<OsLockerControllerPage />}
        />
        <Route path="/os/lockers" element={<OsLockersPage />} />
        <Route
          path="/os/embedded/lockers"
          element={<OsLockersPageEmbedded />}
        />
        <Route path="/os/reservations" element={<OsReservationsPage />} />
        <Route
          path="/os/reservations/:reservationId"
          element={<OsReservationsPage />}
        />
        <Route path="/os/reporting/:subPage" element={<OsReportingPage />} />

        <Route
          path="/os/embedded/configuration"
          element={<OsConfigurationPage />}
        />
        <Route path="/os/configuration" element={<OsConfigurationPage />} />
        <Route path="/os/clients" element={<OsClientPage />} />
        <Route path="/os/clients/:id" element={<OsClientPage />} />
        <Route path="/us/profile" element={<UserProfilePage />} />
        <Route path="/us/organizations" element={<UserOrgsPage />} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            isAllowed={
              isAuthenticated && !!isSuperAdmin && isMblsAuthenticated !== false
            }
            isReady={isReady && !!user}
            redirectPath="/not-found"
            name="Super Admin routes"
          />
        }
      >
        <Route path="/sa/home" element={<SaHomePage />} />
        <Route path="/sa/users" element={<SaUsersPage />} />
        <Route path="/sa/administrators" element={<SaAdministratorsPage />} />
        <Route path="/sa/administrator/:id" element={<SaAdministratorPage />} />
        <Route path="/sa/org-members" element={<SaOrgMembersPage />} />
        <Route path="/sa/tenants" element={<SaTenantsPage />} />
        <Route path="/sa/tenants/:tenantId" element={<SaTenantPage />} />
        <Route path="/sa/lockers" element={<SaLockersPage />} />
        <Route
          path="/sa/reservation-syncs"
          element={<SaReservationSyncsPage />}
        />
        <Route
          path="/sa/reservation-syncs/:runId"
          element={<SaReservationSyncPage />}
        />
        <Route path="/sa/monthly-reports" element={<SaMonthlyReportsPage />} />
        <Route
          path="/sa/monthly-reports/:runId"
          element={<SaMonthlyReportPage />}
        />
        {/* The admin link LOCKER-468's Slack notice posts — same detail page. */}
        <Route
          path="/sa/monthly-extracts/:runId"
          element={<MonthlyExtractLinkRedirect />}
        />
        <Route path="/sa/sl1-import" element={<SaSl1ImportPage />} />
        {/* The report detail is its own route, not a third tab: it is about ONE
            run and needs a URL an operator can paste into a ticket. */}
        <Route
          path="/sa/sl1-import/:jobId"
          element={<SaSl1ImportReportPage />}
        />
        <Route path="/sa/lockers/:lockerId" element={<SaLockerPage />} />
        <Route path="/sa/sms-expeditors" element={<SaSmsExpeditorsPage />} />
        <Route path="/sa/event-log" element={<SaMeventsPage />} />
        <Route path="/sa/admin-logs" element={<SaAdminLogsPage />} />
        <Route path="/sa/seed-trackers" element={<SaSeedTrackersPage />} />
        <Route path="/sa/sst" element={<SaSstPage />} />
        <Route path="/sa/sstv2" element={<SaSstv2Page />} />
        <Route path="/sa/sstv2/traces/:traceId" element={<Sstv2TraceDetailPage />} />
        <Route path="/sa/sstv2/lockers/:lockerId" element={<SaSstv2LockerPage />} />
        <Route path="/sa/sstv2-migration" element={<SaSstv2MigrationPage />} />
        <Route path="/sa/somum" element={<SaSomumPage />} />
        <Route path="/sa/reservations" element={<SaReservationsPage />} />
        <Route
          path="/sa/notif-deliveries"
          element={<SaNotifDeliveriesPage />}
        />

        <Route
          path="/sa/reservation-audit"
          element={<SaReservationAudPage />}
        />
        <Route
          path="/sa/email-expeditors"
          element={<SaEmailExpeditorsPage />}
        />
        <Route path="/sa/deleted-things" element={<SaDeleteThingsPage />} />
        <Route path="/sa/features" element={<SaFeaturesPage />} />
        <Route path="/sa/features/:id" element={<FeaturePage />} />
      </Route>

      {/* catch-all not found page */}
      <Route path="*" element={<NotFound />} />
      <Route path="/not-accessible" element={<NotAccessiblePage />} />
    </SentryRoutes>
    </div>
  )
}

export default App
